import { rewriteTouchpoints, rewriteRequirements } from '../../../api/mastersheetApi';
import { message } from 'antd';

export const aiRewriterService = {
  /**
   * Handle AI rewriting of touchpoints
   * @param {string} year - The year for the spreadsheet
   * @param {string} rewriteOption - The rewrite option ('Custom Rows', 'All Rows', 'Rows with Empty AI Rewritten Text')
   * @param {Array} selectedRows - Array of selected row numbers (for 'Custom Rows' option)
   * @param {Array} filteredData - The filtered data from the spreadsheet
   * @param {Function} onComplete - Callback function to execute after rewriting
   */
  rewriteTouchpoints: async (year, rewriteOption, selectedRows, filteredData, onComplete) => {
    const options = {};

    try {
      switch (rewriteOption) {
        case 'Custom Rows':
          if (!selectedRows || selectedRows.length === 0) {
            message.warning('Please select rows to rewrite');
            return { success: false, message: 'No rows selected' };
          }

          const selectedUnids = selectedRows.map(rowNum => {
            const rowIndex = parseInt(rowNum) - 1;
            if (filteredData[rowIndex]) {
              return filteredData[rowIndex].unid;
            }
            return null;
          }).filter(unid => unid !== null);

          if (selectedUnids.length === 0) {
            message.error('No valid rows selected');
            return { success: false, message: 'No valid rows selected' };
          }

          options.unids = selectedUnids;
          
          const loadingMessage = message.loading('Rewriting selected touchpoints...', 0);
          const result = await rewriteTouchpoints(year, options);
          message.destroy(loadingMessage);

          if (result.updated_touchpoints?.length > 0) {
            message.success(`Successfully rewrote ${result.updated_touchpoints.length} touchpoints`);
            if (onComplete) onComplete();
            return { success: true, result };
          } else {
            message.info('No touchpoints were rewritten');
            return { success: false, message: 'No touchpoints were rewritten' };
          }

        case 'All Rows':
          options.rewrite_all = true;
          message.success('AI rewriting of all touchpoints has started in the background');
          
          rewriteTouchpoints(year, options)
            .then(result => {
              if (result.updated_touchpoints?.length > 0) {
                message.success(`Successfully rewrote ${result.updated_touchpoints.length} touchpoints`);
                if (onComplete) onComplete();
              }
            })
            .catch(error => {
              message.error('Failed to rewrite touchpoints: ' + error.message);
            });
          
          return { success: true, message: 'Background process started' };

        case 'Rows with Empty AI Rewritten Text':
          options.rewrite_empty = true;
          message.success('AI rewriting of empty touchpoints has started in the background');
          
          rewriteTouchpoints(year, options)
            .then(result => {
              if (result.updated_touchpoints?.length > 0) {
                message.success(`Successfully rewrote ${result.updated_touchpoints.length} touchpoints`);
                if (onComplete) onComplete();
              }
            })
            .catch(error => {
              message.error('Failed to rewrite touchpoints: ' + error.message);
            });
          
          return { success: true, message: 'Background process started' };

        default:
          message.error('Invalid rewrite option');
          return { success: false, message: 'Invalid rewrite option' };
      }
    } catch (error) {
      console.error('Error in AI rewrite:', error);
      message.error('Failed to rewrite touchpoints: ' + error.message);
      return { success: false, error: error.message };
    }
  },

  /**
   * Handle AI rewriting of requirements
   * @param {string} year - The year for the spreadsheet
   * @param {string} rewriteOption - The rewrite option ('Custom Rows', 'All Rows', 'Rows with Empty AI Rewritten Text')
   * @param {Array} selectedRows - Array of selected row numbers (for 'Custom Rows' option)
   * @param {Array} filteredData - The filtered data from the spreadsheet
   * @param {Function} onComplete - Callback function to execute after rewriting
   */
  rewriteRequirements: async (year, rewriteOption, selectedRows, filteredData, onComplete) => {
    const options = {};

    try {
      switch (rewriteOption) {
        case 'Custom Rows':
          if (!selectedRows || selectedRows.length === 0) {
            message.warning('Please select rows to rewrite');
            return { success: false, message: 'No rows selected' };
          }

          const selectedUnids = selectedRows.map(rowNum => {
            const rowIndex = parseInt(rowNum) - 1;
            if (filteredData[rowIndex]) {
              return filteredData[rowIndex].unid;
            }
            return null;
          }).filter(unid => unid !== null);

          if (selectedUnids.length === 0) {
            message.error('No valid rows selected');
            return { success: false, message: 'No valid rows selected' };
          }

          options.unids = selectedUnids;
          
          const loadingMessage = message.loading('Rewriting selected requirements...', 0);
          const result = await rewriteRequirements(year, options);
          message.destroy(loadingMessage);

          if (result.updated_requirements?.length > 0) {
            message.success(`Successfully rewrote ${result.updated_requirements.length} requirements`);
            if (onComplete) onComplete();
            return { success: true, result };
          } else {
            message.info('No requirements were rewritten');
            return { success: false, message: 'No requirements were rewritten' };
          }

        case 'All Rows':
          options.rewrite_all = true;
          message.success('AI rewriting of all requirements has started in the background');
          
          rewriteRequirements(year, options)
            .then(result => {
              if (result.updated_requirements?.length > 0) {
                message.success(`Successfully rewrote ${result.updated_requirements.length} requirements`);
                if (onComplete) onComplete();
              }
            })
            .catch(error => {
              message.error('Failed to rewrite requirements: ' + error.message);
            });
          
          return { success: true, message: 'Background process started' };

        case 'Rows with Empty AI Rewritten Text':
          options.rewrite_empty = true;
          message.success('AI rewriting of empty requirements has started in the background');
          
          rewriteRequirements(year, options)
            .then(result => {
              if (result.updated_requirements?.length > 0) {
                message.success(`Successfully rewrote ${result.updated_requirements.length} requirements`);
                if (onComplete) onComplete();
              }
            })
            .catch(error => {
              message.error('Failed to rewrite requirements: ' + error.message);
            });
          
          return { success: true, message: 'Background process started' };

        default:
          message.error('Invalid rewrite option');
          return { success: false, message: 'Invalid rewrite option' };
      }
    } catch (error) {
      console.error('Error in AI rewrite:', error);
      message.error('Failed to rewrite requirements: ' + error.message);
      return { success: false, error: error.message };
    }
  },
};